import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { db, storage } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const AcademySection = () => {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [content, setContent] = useState({
    imageUrl: 'https://readdy.ai/api/search-image?query=online%20learning%20academy%20digital%20education%20platform%20laptop%20screen%20courses%20purple%20blue%20glowing%20modern%20futuristic%20latina%20students%20success&width=600&height=450&seq=academy1&orientation=landscape'
  });
  const [academyUrl, setAcademyUrl] = useState('https://smartbusiness.academy');

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const docRef = doc(db, 'landing_content', 'academy');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setContent(docSnap.data() as typeof content);
        }
        
        const settingsRef = doc(db, 'settings', 'general');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists() && settingsSnap.data().academyUrl) {
          setAcademyUrl(settingsSnap.data().academyUrl);
        }
      } catch (error) {
        console.error('Error fetching academy content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const handleSave = async () => {
    setUploading(true);
    try {
      let imageUrl = content.imageUrl;

      if (imageFile) {
        const storageRef = ref(storage, `landing/academy_${Date.now()}_${imageFile.name}`);
        const snapshot = await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      const dataToSave = { ...content, imageUrl };
      await setDoc(doc(db, 'landing_content', 'academy'), dataToSave);
      setContent(dataToSave);
      setIsEditing(false);
      setImageFile(null);
    } catch (error) {
      console.error('Error saving academy data:', error);
      alert('Error al guardar los cambios.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="relative py-24 overflow-hidden bg-[#050008]">
      
      {isAdmin && (
        <div className="absolute top-8 right-4 z-50 flex gap-2">
          {!isEditing ? (
            <button 
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
            >
              <i className="ri-edit-line" />
              Editar Imagen
            </button>
          ) : (
            <>
              <button 
                onClick={handleSave}
                disabled={uploading}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg disabled:opacity-50"
              >
                {uploading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                {uploading ? 'Guardando...' : 'Guardar'}
              </button>
              <button 
                onClick={() => { setIsEditing(false); setImageFile(null); }}
                disabled={uploading}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg disabled:opacity-50"
              >
                <i className="ri-close-line" />
                Cancelar
              </button>
            </>
          )}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-8">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Left Content */}
          <div className="flex-1 text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#8c52ff]/10 border border-[#8c52ff]/30 rounded-full px-4 py-2 mb-6">
              <i className="ri-graduation-cap-line text-[#8c52ff] text-sm" />
              <span className="text-[#8c52ff] text-xs font-bold uppercase tracking-widest">{t('home.academy.badge')}</span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black text-white mb-6 leading-tight">
              {t('home.academy.titleMain')}
              <span className="block bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] bg-clip-text text-transparent">
                {t('home.academy.titleHighlight')}
              </span>
            </h2>

            <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              {t('home.academy.subtitle')}
            </p>

            <ul className="space-y-3 mb-10 text-left max-w-md mx-auto lg:mx-0">
              {(t('home.academy.benefits', { returnObjects: true }) as string[]).map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#8c52ff]/20 flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#8c52ff] text-xs" />
                  </div>
                  <span className="text-white/70 text-sm">{item}</span>
                </li>
              ))}
            </ul>

            <a
              href={academyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap inline-flex items-center gap-3 bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white font-bold px-8 py-4 rounded-full hover:opacity-90 hover:scale-105 transition-all duration-300 text-base shadow-lg shadow-purple-500/30 cursor-pointer"
            >
              <i className="ri-graduation-cap-line" />
              {t('home.academy.button')}
              <i className="ri-external-link-line text-sm" />
            </a>
          </div>

          {/* Right Visual */}
          <div className="flex-1 relative flex justify-center">
            <div className="relative w-full max-w-lg">
              {/* Main image */}
              <div className="relative rounded-3xl overflow-hidden border border-white/10">
                <img
                  src={imageFile ? URL.createObjectURL(imageFile) : content.imageUrl}
                  alt="Smart Business Academy"
                  className="w-full h-96 md:h-[480px] object-cover object-top"
                />
                
                {isEditing && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-20">
                    <div className="w-4/5 text-center p-4">
                      <p className="text-white text-xs mb-2">Sube una nueva imagen:</p>
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={(e) => setImageFile(e.target.files ? e.target.files[0] : null)}
                        className="w-full bg-white/10 border border-purple-500/50 rounded px-2 py-1 text-white text-xs focus:outline-none mb-2"
                      />
                      <p className="text-white/50 text-xs">O pega la URL:</p>
                      <input 
                        type="text" 
                        value={content.imageUrl} 
                        onChange={(e) => setContent({...content, imageUrl: e.target.value})}
                        className="w-full bg-white/10 border border-purple-500/50 rounded px-2 py-1 text-white text-xs focus:outline-none"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                )}

                {!isEditing && (
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050008]/80 via-transparent to-transparent pointer-events-none" />
                )}
                {!isEditing && (
                  <div className="absolute bottom-6 left-6 right-6 pointer-events-none">
                    <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-[#8c52ff] to-[#00c2ff]">
                          <i className="ri-play-line text-white" />
                        </div>
                        <div>
                          <div className="text-white font-bold text-sm">{t('home.academy.visual.module')}</div>
                          <div className="text-white/50 text-xs">{t('home.academy.visual.topic')}</div>
                        </div>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-1.5">
                        <div className="bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] h-1.5 rounded-full w-3/5" />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-white/40 text-xs">60% {t('home.academy.visual.completed')}</span>
                        <span className="text-[#8c52ff] text-xs font-semibold">{t('home.academy.visual.status')}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Floating stats */}
              {!isEditing && (
                <>
                  <div className="absolute -top-4 -right-4 bg-black/80 backdrop-blur-xl border border-[#FF2DAA]/30 rounded-2xl px-4 py-3 pointer-events-none">
                    <div className="text-[#FF2DAA] font-black text-2xl">50</div>
                    <div className="text-white/50 text-xs">{t('home.academy.stats.students')}</div>
                  </div>
                  <div className="absolute -bottom-4 -left-4 bg-black/80 backdrop-blur-xl border border-[#00c2ff]/30 rounded-2xl px-4 py-3 pointer-events-none">
                    <div className="flex items-center gap-1 mb-1">
                      {[1,2,3,4,5].map((s) => (
                        <i key={s} className="ri-star-fill text-yellow-400 text-xs" />
                      ))}
                    </div>
                    <div className="text-white/50 text-xs">4.9/5 {t('home.academy.stats.rating')}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AcademySection;
